import { useMemo } from 'react'
import styled from 'styled-components'

import { fromMd } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../containers/EmbroideryService'
import { EmbroideryPlacementDetails, EmbroideryPlacementOffset } from '../../../../types/graphql'

export const LogoPreviewItem = <
  T extends { logoPlacement: EmbroideryPlacementDetails | undefined },
>({
  values,
}: {
  values: T
}) => {
  const { backgroundImage, selectedEmbroideryFormOptions } = EmbroideryService.useContainer()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures },
      },
    },
  } = useTranslation()

  const backgroundImageOffsetsByKey: Record<string, EmbroideryPlacementOffset> =
    backgroundImage?.embroideryOffsets || {}

  const backgroundImageOffsets: EmbroideryPlacementOffset | undefined =
    values.logoPlacement && backgroundImageOffsetsByKey[values.logoPlacement.offsetKey ?? '']

  // Avoid overlapping logo & text embroidery when configured with same placement
  const verticalOverlapOffset = useMemo(() => {
    const hasPlacementOverlap =
      selectedEmbroideryFormOptions?.textPlacement === values.logoPlacement?.name
    if (hasPlacementOverlap) return '-5%'
    return '0%'
  }, [selectedEmbroideryFormOptions?.textPlacement, values.logoPlacement?.name])

  const xOffset = backgroundImageOffsets?.x ?? '55%'
  const yOffset = `calc(${backgroundImageOffsets?.y ?? '33%'} + ${verticalOverlapOffset})`
  const rotation = backgroundImageOffsets?.rotation ?? 0
  const scale = backgroundImageOffsets?.scale ?? 1

  if (!values.logoPlacement) return null

  return (
    <Container
      style={{
        left: xOffset,
        top: yOffset,
        transform: `translate(-50%, -50%) rotateZ(${rotation}deg) scale(${scale})`,
      }}
    >
      <TextWrapper>
        <PreviewText>{fixtures.previewItemText}</PreviewText>
      </TextWrapper>
    </Container>
  )
}

const Container = styled.div`
  /* Set font size on container so that rest of spacing properties can be based on container size */
  font-size: clamp(8px, 2vw, 16px);
  position: absolute;
  z-index: ${o => o.theme.zIndex.foregroundContent};
  border: 2px dashed ${o => o.theme.color.deprecated.white.primary};
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.2);
  padding: 0.55em 1.5em;
  text-align: center;
  max-height: 3em;
  ${fromMd} {
    /* Adjust clamped font size once preview container is displayed half-width on >md */
    font-size: clamp(8px, 1vw, 16px);
    max-width: 6.56em;
  }
`
const TextWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
`

const PreviewText = styled.span`
  line-height: 1.5em;
  font-weight: ${o => o.theme.typography.font.weight[700]};
  text-transform: uppercase;
  color: ${o => o.theme.color.deprecated.white.primary};
`
